import { NavLink } from "react-router-dom";

import { KpiRail } from "../components/KpiRail";
import { LastUpdatedLabel } from "../components/LastUpdatedLabel";
import { NumberedListCard } from "../components/NumberedListCard";
import { PremiumHero } from "../components/PremiumHero";
import { SignalPill } from "../components/SignalPill";
import { useContent } from "../hooks/useContent";
import { PageContainer } from "../layout/PageContainer";
import { api } from "../lib/api";

function uniqueItems(items: string[]): string[] {
  const seen = new Set<string>();

  return items
    .map((item) => item.trim())
    .filter((item) => {
      if (!item) {
        return false;
      }

      const key = item.toLowerCase();
      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
}

function toSentence(text: string): string {
  const trimmed = text.trim();
  const firstSentence = trimmed.match(/.*?[.?!](?:\s|$)/);
  return firstSentence ? firstSentence[0].trim() : trimmed;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0
  }).format(value);
}

function ReviewPathCard({
  title,
  body,
  href
}: {
  title: string;
  body: string;
  href: string;
}) {
  return (
    <NavLink
      to={href}
      style={{
        display: "block",
        textDecoration: "none",
        color: "inherit",
        borderRadius: "24px",
        padding: "22px",
        background: "linear-gradient(180deg, rgba(11, 17, 30, 0.92), rgba(8, 12, 22, 0.8))",
        border: "1px solid rgba(255,255,255,0.08)",
        boxShadow: "0 18px 40px rgba(3, 8, 20, 0.18)"
      }}
    >
      <div className="stack-sm">
        <span className="card-eyebrow">Go Deeper</span>
        <h3 className="card-title">{title}</h3>
        <p className="card-copy">{body}</p>
      </div>
    </NavLink>
  );
}

export function OverviewPage() {
  const { data, loading, error } = useContent("investment-case-page", async () => {
    const [recommendation, overview, site, economics, risk] = await Promise.all([
      api.getRecommendation(),
      api.getOverview(),
      api.getSite(),
      api.getEconomics(),
      api.getRisk()
    ]);

    return { recommendation, overview, site, economics, risk };
  });

  if (!data) {
    return (
      <PageContainer
        eyebrow="Investment Case"
        title="Compact infill thesis. Visible controls."
        lede="The lead concept remains ahead because the proof stack is concrete enough to continue and the remaining gates are finite."
      >
        {loading ? <div className="loading-panel shimmer-card">Loading investment case...</div> : null}
        {error ? <div className="error-panel">{error}</div> : null}
      </PageContainer>
    );
  }

  const utilityBaseline = formatCurrency(data.economics.costSummary.utilityBaseline.citySideKnownBaseline);
  const thesis =
    toSentence(data.recommendation.why[0] ?? data.overview.executiveSummary) ||
    "The current proof stack supports disciplined continuation under one lead concept.";
  const supportSignals = uniqueItems([
    data.site.siteSummary.leadConceptFit,
    data.economics.costSummary.leadConceptCostFit,
    ...data.recommendation.topPositives,
    ...data.overview.topStrengths
  ]).slice(0, 4);
  const gatingSignals = uniqueItems([
    ...data.recommendation.topCautions,
    ...data.risk.riskSummary.remainingOpenRisks,
    ...data.economics.costSummary.openCostRisks,
    ...data.economics.operationsSummary.openOperatingItems
  ]).slice(0, 4);
  const totalGates =
    data.risk.riskSummary.remainingOpenRisks.length +
    data.economics.costSummary.openCostRisks.length +
    data.economics.operationsSummary.openOperatingItems.length;

  return (
    <PageContainer
      eyebrow="Investment Case"
      title="Compact infill thesis. Visible controls."
      lede="One lead concept is still clearly ahead. The current question is execution discipline, not whether a viable case exists."
    >
      {loading ? <div className="loading-panel shimmer-card">Loading investment case...</div> : null}
      {error ? <div className="error-panel">{error}</div> : null}
      <div className="stack-xl">
        <PremiumHero
          eyebrow="Investor Thesis"
          title="A controlled go-forward case now exists at 400 Norwood."
          subtitle={thesis}
          backgroundImage="/comps/104-f-st-streetview.jpg"
          backgroundLabel="Grounded local comp context imagery"
          badges={
            <>
              <SignalPill label={data.recommendation.label} tone="warning" />
              <SignalPill label={data.recommendation.confidencePosture} tone="neutral" />
              <SignalPill label={data.overview.leadConcept} tone="positive" />
            </>
          }
          actions={
            <>
              <NavLink to="/financials" className="link-button link-button-primary">
                Open Financials
              </NavLink>
              <NavLink to="/site-zoning" className="link-button link-button-secondary">
                Open Site & Zoning
              </NavLink>
              <NavLink to="/risks-mitigants" className="link-button link-button-secondary">
                Open Risks & Mitigants
              </NavLink>
            </>
          }
          aside={
            <div className="stack-md">
              <div className="stack-xs">
                <span className="card-eyebrow">At a Glance</span>
                <div style={{ fontSize: "1.8rem", lineHeight: 1, color: "#f6fbff", letterSpacing: "-0.04em" }}>
                  {data.recommendation.label}
                </div>
                <p style={{ margin: 0, color: "rgba(223, 233, 248, 0.78)", lineHeight: 1.5 }}>
                  {data.risk.riskSummary.riskPosture}
                </p>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: "12px"
                }}
              >
                {[
                  ["Lead concept", data.overview.leadConcept],
                  ["Fallback", data.overview.fallbackConcept],
                  ["Open gates", `${totalGates}`],
                  ["Utility anchor", utilityBaseline]
                ].map(([label, value]) => (
                  <div
                    key={label}
                    style={{
                      borderRadius: "18px",
                      padding: "14px",
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.08)"
                    }}
                  >
                    <div style={{ fontSize: "0.72rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(198,211,234,0.62)" }}>
                      {label}
                    </div>
                    <div style={{ marginTop: "8px", color: "#f4f8ff", lineHeight: 1.35 }}>{value}</div>
                  </div>
                ))}
              </div>
              <div
                style={{
                  overflow: "hidden",
                  borderRadius: "18px",
                  border: "1px solid rgba(255,255,255,0.08)",
                  background: "rgba(255,255,255,0.04)"
                }}
              >
                <img src="/comps/104-f-st-map.jpg" alt="Local context map near 400 Norwood" style={{ display: "block", width: "100%", height: "160px", objectFit: "cover" }} />
              </div>
              <LastUpdatedLabel value={data.overview.lastUpdated} />
            </div>
          }
        />

        <KpiRail
          items={[
            {
              label: "Recommendation",
              value: data.recommendation.label,
              note: "Current public call",
              tone: "warning"
            },
            {
              label: "Lead concept",
              value: data.overview.leadConcept,
              note: "Best current fit",
              tone: "positive"
            },
            {
              label: "Conviction",
              value: data.recommendation.confidencePosture,
              note: "Structured, not final",
              tone: "neutral"
            },
            {
              label: "Open gates",
              value: `${totalGates}`,
              note: "Finite issues still controlling the decision",
              tone: "warning"
            }
          ]}
        />

        <div className="grid-two">
          <NumberedListCard eyebrow="Why It Clears Now" title="The current proof stack is real." items={supportSignals} tone="positive" />
          <NumberedListCard eyebrow="What Still Decides It" title="The remaining gates are explicit." items={gatingSignals} tone="warning" />
        </div>

        <section
          style={{
            borderRadius: "28px",
            padding: "24px",
            background: "linear-gradient(180deg, rgba(8, 13, 24, 0.94), rgba(7, 11, 20, 0.82))",
            border: "1px solid rgba(255,255,255,0.08)",
            boxShadow: "0 20px 50px rgba(3, 8, 20, 0.22)"
          }}
        >
          <div className="stack-md">
            <div className="stack-xs">
              <span className="card-eyebrow">Next Investor Path</span>
              <h2 className="card-title">Open the three screens that can still change the answer.</h2>
            </div>
            <div
              style={{
                display: "grid",
                gap: "16px",
                gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))"
              }}
            >
              <ReviewPathCard title="Financials" body="Budget discipline, scenario spread, and downside posture." href="/financials" />
              <ReviewPathCard title="Site & Zoning" body="Parcel fit, regulatory fit, and remaining land-sensitive gates." href="/site-zoning" />
              <ReviewPathCard title="Risks & Mitigants" body="Managed downside, active mitigants, and kill triggers." href="/risks-mitigants" />
            </div>
          </div>
        </section>
      </div>
    </PageContainer>
  );
}
